import type { FC, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import type { ResolvedTheme, ThemeMode } from './types'
import { ThemeContext, type ThemeContextValue } from './ThemeContext'
import { getThemeByMode } from './themes'

const STORAGE_KEY = 'claude-dashboard-theme'
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'

interface ThemeProviderProps {
  readonly children: ReactNode
}

export const ThemeProvider: FC<ThemeProviderProps> = ({ children }) => {
  const [modeState, setModeState] = useState<ThemeMode>(getStoredMode)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    modeState === 'system' ? resolveSystemTheme() : modeState,
  )

  useEffect(() => {
    setResolvedTheme(modeState === 'system' ? resolveSystemTheme() : modeState)
  }, [modeState])

  const handleSystemChange = useCallback((resolved: ResolvedTheme) => {
    setResolvedTheme(resolved)
  }, [])

  useSystemThemeListener(modeState, handleSystemChange)

  const setMode = useCallback((newMode: ThemeMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, newMode)
    } catch {
      // localStorage unavailable
    }
    setModeState(newMode)
  }, [])

  const theme = useMemo(() => getThemeByMode(resolvedTheme), [resolvedTheme])

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      mode: modeState,
      resolvedTheme,
      theme,
      setMode,
    }),
    [modeState, resolvedTheme, theme, setMode],
  )

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}

function useSystemThemeListener(
  mode: ThemeMode,
  onSystemChange: (resolved: ResolvedTheme) => void,
): void {
  useEffect(() => {
    if (mode !== 'system') return

    const mediaQuery = globalThis.matchMedia(DARK_MEDIA_QUERY)
    const handleChange = (e: MediaQueryListEvent) => {
      onSystemChange(e.matches ? 'dark' : 'light')
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [mode, onSystemChange])
}

function getStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
  } catch {
    // localStorage unavailable
  }
  return 'system'
}

function resolveSystemTheme(): ResolvedTheme {
  return typeof globalThis !== 'undefined' && globalThis.matchMedia?.(DARK_MEDIA_QUERY).matches
    ? 'dark'
    : 'light'
}
